import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { SkinAnalysisPredictValidationClient } from '@/infrastructure/SkinAnalysisPredictValidationClient';
import type {
  SkinAnalysisValidationPromptConfig,
  SkinAnalysisValidationPromptKey,
  SkinAnalysisValidationPromptTemplates,
} from '@/models/SkinAnalysisValidationCopyPrompt';

const PROMPT_QUERY_KEY = ['skin-analysis-validation', 'prompts-v3'] as const;

function toErrorMessage(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { message?: unknown } | undefined;
    if (typeof data?.message === 'string') {
      return data.message;
    }
  }
  return error instanceof Error && error.message ? error.message : fallback;
}

/** Editor state for the complete Admin-preview prompt snapshot. */
export const useSkinAnalysisValidationCopyPrompts = () => {
  const client = useMemo(() => new SkinAnalysisPredictValidationClient(), []);
  const queryClient = useQueryClient();
  const configQuery = useQuery({
    queryKey: PROMPT_QUERY_KEY,
    queryFn: () => client.getPrompts(),
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
  const [config, setConfig] = useState<SkinAnalysisValidationPromptConfig | null>(null);
  const [systemTemplates, setSystemTemplates] = useState<SkinAnalysisValidationPromptTemplates | null>(null);
  const [templates, setTemplates] = useState<SkinAnalysisValidationPromptTemplates | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const appliedFingerprint = useRef<string | null>(null);

  useEffect(() => {
    if (!configQuery.data) {
      return;
    }
    if (appliedFingerprint.current === configQuery.data.fingerprint) {
      return;
    }
    const hasPendingEdits = Boolean(
      systemTemplates &&
        templates &&
        config &&
        (Object.keys(config.templates) as SkinAnalysisValidationPromptKey[]).some(
          (key) => systemTemplates[key] !== config.systemTemplates[key] || templates[key] !== config.templates[key],
        ),
    );
    if (hasPendingEdits) {
      setError(
        'The stored prompt snapshot changed while you had unsaved edits. Discard edits to load it, or save to replace it.',
      );
      return;
    }
    appliedFingerprint.current = configQuery.data.fingerprint;
    setConfig(configQuery.data);
    setSystemTemplates({ ...configQuery.data.systemTemplates });
    setTemplates({ ...configQuery.data.templates });
    setError(null);
  }, [config, configQuery.data, systemTemplates, templates]);

  const isDirty = useMemo(
    () =>
      Boolean(
        systemTemplates &&
          templates &&
          config &&
          (Object.keys(config.templates) as SkinAnalysisValidationPromptKey[]).some(
            (key) => systemTemplates[key] !== config.systemTemplates[key] || templates[key] !== config.templates[key],
          ),
      ),
    [config, systemTemplates, templates],
  );

  const updateSystemTemplate = useCallback((key: SkinAnalysisValidationPromptKey, value: string) => {
    setSystemTemplates((current) => (current ? { ...current, [key]: value } : current));
  }, []);

  const updateTemplate = useCallback((key: SkinAnalysisValidationPromptKey, value: string) => {
    setTemplates((current) => (current ? { ...current, [key]: value } : current));
  }, []);

  const resetTemplateToCodeDefault = useCallback(
    (key: SkinAnalysisValidationPromptKey) => {
      const definition = config?.definitions.find((candidate) => candidate.key === key);
      if (definition) {
        updateTemplate(key, definition.defaultTemplate);
      }
    },
    [config, updateTemplate],
  );

  const resetSystemTemplateToCodeDefault = useCallback(
    (key: SkinAnalysisValidationPromptKey) => {
      const definition = config?.definitions.find((candidate) => candidate.key === key);
      if (definition) {
        updateSystemTemplate(key, definition.defaultSystemTemplate);
      }
    },
    [config, updateSystemTemplate],
  );

  const revertEdits = useCallback(() => {
    setSystemTemplates(config ? { ...config.systemTemplates } : null);
    setTemplates(config ? { ...config.templates } : null);
    setError(null);
  }, [config]);

  const save = useCallback(async () => {
    if (!systemTemplates || !templates || isSaving) {
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      const saved = await client.savePrompts({ systemTemplates, templates });
      appliedFingerprint.current = saved.fingerprint;
      queryClient.setQueryData(PROMPT_QUERY_KEY, saved);
      setConfig(saved);
      setSystemTemplates({ ...saved.systemTemplates });
      setTemplates({ ...saved.templates });
    } catch (reason) {
      setError(toErrorMessage(reason, 'Failed to save the validation prompts.'));
      throw reason;
    } finally {
      setIsSaving(false);
    }
  }, [client, isSaving, queryClient, systemTemplates, templates]);

  const resetToCodeDefaults = useCallback(async () => {
    if (isResetting) {
      return;
    }
    setIsResetting(true);
    setError(null);
    try {
      const defaults = await client.resetPrompts();
      appliedFingerprint.current = defaults.fingerprint;
      queryClient.setQueryData(PROMPT_QUERY_KEY, defaults);
      setConfig(defaults);
      setSystemTemplates({ ...defaults.systemTemplates });
      setTemplates({ ...defaults.templates });
    } catch (reason) {
      setError(toErrorMessage(reason, 'Failed to restore the code default prompts.'));
      throw reason;
    } finally {
      setIsResetting(false);
    }
  }, [client, isResetting, queryClient]);

  return {
    config,
    systemTemplates,
    templates,
    isLoading: configQuery.isPending,
    isSaving,
    isResetting,
    isDirty,
    error: error ?? (configQuery.error ? toErrorMessage(configQuery.error, 'Failed to load the prompts.') : null),
    updateTemplate,
    updateSystemTemplate,
    resetTemplateToCodeDefault,
    resetSystemTemplateToCodeDefault,
    revertEdits,
    save,
    resetToCodeDefaults,
    reload: configQuery.refetch,
  };
};
